import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { UPSTREAM_DATA_OUTPUTS_KEY } from '@/utils/queryKeys.ts';
import { AxiosError } from 'axios';
import { get_upstream_data_outputs_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { StepDataOutputs } from '@/types/automationFields.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useRetrieveEditorLocalSubflowContext from '@/hooks/api/automations/useRetrieveEditorLocalSubflowContext.ts';

const useRetrieveAllUpstreamDataOutputs = (
  scopeOverride?: 'root' | 'local_subflow',
): UseQueryResult<StepDataOutputs, AxiosError> => {
  const apiClient = useApiClient();

  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const automationId = useAutomationId();
  const latestVersionId = latestVersion?.id;
  const { target } = useActiveCompositionTarget();
  const scope = scopeOverride ?? target.scope_kind;
  const subflowId = scope === 'local_subflow' && target.scope_kind === 'local_subflow' ? target.subflow_id : undefined;
  const localContext = useRetrieveEditorLocalSubflowContext(subflowId);

  const rootQuery = useQuery<StepDataOutputs, AxiosError>({
    queryKey: [...UPSTREAM_DATA_OUTPUTS_KEY(automationId, latestVersionId || ''), scope],
    queryFn: async () => {
      const response = await apiClient.get(get_upstream_data_outputs_url(latestVersionId));
      return response.data;
    },
    enabled: !!automationId && !!latestVersionId && scope === 'root',
    placeholderData: keepPreviousData,
  });

  if (scope === 'local_subflow') {
    return {
      ...localContext,
      data: localContext.data ? { step_data_outputs: localContext.data.step_data_outputs } : undefined,
    } as UseQueryResult<StepDataOutputs, AxiosError>;
  }

  return rootQuery;
};

export default useRetrieveAllUpstreamDataOutputs;
